import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   FamilyPlanningStyles — Styled components for
   Family Planning Module (Task 17).
   Theme: Deep purple/violet (#6a1b9a) — reproductive health
   ═══════════════════════════════════════════════════════════════ */

const PRIMARY = "#6a1b9a";
const PRIMARY_LIGHT = "#8e24aa";
const PRIMARY_BG = "#f3e5f5";
const SECONDARY = "#283593";
const SUCCESS = "#2e7d32";
const WARNING = "#f57f17";
const DANGER = "#c62828";
const INFO = "#0277bd";
const TEAL = "#00695c";

/* ─── Page Wrapper ─── */
export const FPPageWrapper = styled("div")({
    padding: "16px 20px 32px",
    background: "#f5f6fa",
    minHeight: "calc(100vh - 120px)",
    "@media(max-width: 768px)": { padding: "10px 8px 24px" },
});

/* ─── Header ─── */
export const FPHeader = styled("div")({
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "16px",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    "@media(max-width: 768px)": { padding: "14px 16px", flexDirection: "column", alignItems: "flex-start" },
});

export const FPHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "4px" });
export const FPHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#fff" });
export const FPHeaderSub = styled("p")({ margin: 0, fontSize: "0.85rem", opacity: 0.85 });

export const FPHeaderStats = styled("div")({ display: "flex", gap: "10px", flexWrap: "wrap" });

export const FPStatChip = styled("div")(function (props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    if (props.variant === "info") { bg = "rgba(2,119,189,0.3)"; border = "rgba(2,119,189,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

/* ─── Tab Bar ─── */
export const FPTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const FPTabBtn = styled("button")(function (props) {
    const activeBg = props.color || PRIMARY;
    return {
        flex: "1 1 auto",
        padding: "10px 18px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.active ? 700 : 500,
        background: props.active ? activeBg : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? activeBg : "#f5f5f5" },
    };
});

export const FPTabBadge = styled("span")(function (props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "20px",
        height: "20px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 700,
        background: props.active ? "rgba(255,255,255,0.3)" : "#e0e0e0",
        color: props.active ? "#fff" : "#666",
        padding: "0 6px",
    };
});

/* ─── Loading / Empty ─── */
export const FPLoading = styled("div")({
    padding: "60px 20px",
    textAlign: "center",
    color: "#888",
    fontSize: "1rem",
});

export const FPEmptyState = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
});

export const FPEmptyIcon = styled("div")({ fontSize: "3rem", marginBottom: "12px" });
export const FPEmptyTitle = styled("h3")({ margin: 0, color: "#333", fontSize: "1.2rem", marginBottom: "8px" });
export const FPEmptyDesc = styled("p")({ margin: 0, color: "#777", fontSize: "0.9rem", maxWidth: "440px", marginLeft: "auto", marginRight: "auto" });

/* ─── Buttons ─── */
export const FPPrimaryBtn = styled("button")({
    padding: "10px 24px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
    background: PRIMARY,
    color: "#fff",
    transition: "all 0.2s",
    ":hover": { background: PRIMARY_LIGHT, transform: "translateY(-1px)" },
    ":disabled": { opacity: 0.5, cursor: "not-allowed", transform: "none" },
});

export const FPSecondaryBtn = styled("button")({
    padding: "10px 24px",
    border: "1px solid " + PRIMARY,
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
    background: "#fff",
    color: PRIMARY,
    transition: "all 0.2s",
    ":hover": { background: PRIMARY_BG },
});

export const FPDangerBtn = styled("button")({
    padding: "8px 18px",
    border: "1px solid " + DANGER,
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.82rem",
    fontWeight: 600,
    background: "#fff",
    color: DANGER,
    transition: "all 0.2s",
    ":hover": { background: "#ffebee" },
});

/* ─── Section Card ─── */
export const FPSectionCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "16px",
    overflow: "hidden",
});

export const FPSectionHeader = styled("div")(function (props) {
    const bg = props.color || PRIMARY;
    return {
        padding: "12px 18px",
        background: bg,
        color: "#fff",
        fontSize: "0.9rem",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };
});

export const FPSectionBody = styled("div")({
    padding: "18px",
});

/* ─── Form Row / Grid ─── */
export const FPFormRow = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: props.cols || "1fr 1fr",
        gap: "14px",
        marginBottom: "14px",
        "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
    };
});

export const FPFormGroup = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
});

export const FPLabel = styled("label")({
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#555",
    marginBottom: "2px",
});

export const FPInput = styled("input")({
    padding: "9px 12px",
    border: "1.5px solid #ddd",
    borderRadius: "8px",
    fontSize: "0.85rem",
    outline: "none",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
});

export const FPSelect = styled("select")({
    padding: "9px 12px",
    border: "1.5px solid #ddd",
    borderRadius: "8px",
    fontSize: "0.85rem",
    outline: "none",
    background: "#fff",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
});

export const FPTextarea = styled("textarea")({
    padding: "9px 12px",
    border: "1.5px solid #ddd",
    borderRadius: "8px",
    fontSize: "0.85rem",
    outline: "none",
    resize: "vertical",
    minHeight: "70px",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
});

/* ─── Checkbox / Toggle ─── */
export const FPCheckRow = styled("label")(function (props) {
    const isChecked = props.checked;
    return {
        display: "flex",
        // alignItems: "center",
        gap: "8px",
        // padding: "8px 12px",
        // border: "1.5px solid " + (isChecked ? PRIMARY : "#e0e0e0"),
        // borderRadius: "8px",
        cursor: "pointer",
        // background: isChecked ? PRIMARY_BG : "#fff",
        transition: "all 0.2s",
        fontSize: "0.82rem",
        // color: isChecked ? PRIMARY : "#444",
        // fontWeight: isChecked ? 600 : 400,
        // ":hover": { borderColor: PRIMARY_LIGHT, background: isChecked ? PRIMARY_BG : "#fafafa" },
    };
});

export const FPCheckGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "8px",
    marginBottom: "12px",
});

/* ─── Method Selection Grid ─── */
export const FPMethodGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "10px",
    marginBottom: "16px",
});

export const FPMethodCard = styled("div")(function (props) {
    const isSelected = props.selected;
    const categoryColor = props.categoryColor || PRIMARY;
    return {
        padding: "14px 16px",
        border: "2px solid " + (isSelected ? categoryColor : "#e0e0e0"),
        borderRadius: "10px",
        cursor: "pointer",
        background: isSelected ? (categoryColor + "10") : "#fff",
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        position: "relative",
        ":hover": { borderColor: categoryColor, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
    };
});

export const FPMethodName = styled("div")({
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#333",
});

export const FPMethodMeta = styled("div")({
    fontSize: "0.72rem",
    color: "#888",
});

export const FPMethodBadge = styled("span")(function (props) {
    return {
        position: "absolute",
        top: "8px",
        right: "8px",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.65rem",
        fontWeight: 700,
        background: props.color || PRIMARY,
        color: "#fff",
    };
});

/* ─── Category Header ─── */
export const FPCategoryHeader = styled("div")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        background: (props.color || PRIMARY) + "12",
        borderRadius: "8px",
        marginBottom: "10px",
        marginTop: "6px",
        fontSize: "0.85rem",
        fontWeight: 700,
        color: props.color || PRIMARY,
    };
});

/* ─── Alert Banner ─── */
export const FPAlertBanner = styled("div")(function (props) {
    const type = props.type || "info";
    const bgMap = { critical: "#ffebee", warning: "#fff8e1", info: "#e3f2fd", success: "#e8f5e9" };
    const borderMap = { critical: "#ef9a9a", warning: "#ffe082", info: "#90caf9", success: "#a5d6a7" };
    const colorMap = { critical: "#c62828", warning: "#e65100", info: "#1565c0", success: "#2e7d32" };
    return {
        padding: "12px 16px",
        borderRadius: "8px",
        border: "1px solid " + (borderMap[type] || borderMap.info),
        background: bgMap[type] || bgMap.info,
        marginBottom: "12px",
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        color: colorMap[type] || colorMap.info,
    };
});

export const FPAlertIcon = styled("span")({
    fontSize: "1.2rem",
    flexShrink: 0,
    marginTop: "2px",
});

export const FPAlertContent = styled("div")({
    flex: 1,
});

export const FPAlertTitle = styled("div")({
    fontWeight: 700,
    fontSize: "0.85rem",
    marginBottom: "2px",
});

export const FPAlertDetail = styled("div")({
    fontSize: "0.8rem",
    opacity: 0.9,
});

export const FPAlertAction = styled("div")({
    fontSize: "0.78rem",
    marginTop: "4px",
    fontStyle: "italic",
    opacity: 0.85,
});

/* ─── Timeline ─── */
export const FPTimelineContainer = styled("div")({
    position: "relative",
    paddingLeft: "32px",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "14px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "#e0e0e0",
    },
});

export const FPTimelineItem = styled("div")(function (props) {
    const statusColor = props.statusColor || PRIMARY;
    return {
        position: "relative",
        marginBottom: "20px",
        "&::before": {
            content: '""',
            position: "absolute",
            left: "-24px",
            top: "14px",
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            background: statusColor,
            border: "2px solid #fff",
            boxShadow: "0 0 0 2px " + statusColor,
            zIndex: 1,
        },
    };
});

export const FPTimelineCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    padding: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #f0f0f0",
});

export const FPTimelineDate = styled("div")({
    fontSize: "0.75rem",
    color: "#888",
    fontWeight: 600,
    marginBottom: "6px",
});

export const FPTimelineMethod = styled("div")({
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#333",
    marginBottom: "4px",
});

export const FPTimelineMeta = styled("div")({
    fontSize: "0.8rem",
    color: "#666",
    marginBottom: "6px",
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
});

export const FPTimelineTag = styled("span")(function (props) {
    return {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: (props.color || PRIMARY) + "18",
        color: props.color || PRIMARY,
    };
});

export const FPTimelineActions = styled("div")({
    display: "flex",
    gap: "8px",
    marginTop: "10px",
});

export const FPTimelineBtn = styled("button")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "5px 12px",
        border: "1px solid " + color,
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "0.75rem",
        fontWeight: 600,
        background: "#fff",
        color: color,
        transition: "all 0.2s",
        ":hover": { background: color + "10" },
    };
});

/* ─── Eligibility Panel ─── */
export const FPEligibilityPanel = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
    overflow: "hidden",
    marginBottom: "16px",
});

export const FPEligibilityHeader = styled("div")({
    padding: "14px 18px",
    background: "linear-gradient(135deg, #e65100, #f57c00)",
    color: "#fff",
    fontSize: "0.95rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
});

export const FPEligibilityBody = styled("div")({
    padding: "16px 18px",
});

export const FPEligibilityList = styled("ul")({
    margin: "6px 0",
    paddingLeft: "20px",
    fontSize: "0.82rem",
    color: "#555",
    lineHeight: 1.8,
});

export const FPEligibilityLabel = styled("div")(function (props) {
    const color = props.type === "contraindication" ? DANGER : WARNING;
    return {
        fontWeight: 700,
        fontSize: "0.82rem",
        color: color,
        marginTop: "10px",
        marginBottom: "4px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    };
});

/* ─── Renewal Calendar ─── */
export const FPRenewalCard = styled("div")(function (props) {
    const urgency = props.urgency || "normal";
    const bgMap = { overdue: "#ffebee", soon: "#fff8e1", normal: "#e8f5e9" };
    const borderMap = { overdue: "#ef9a9a", soon: "#ffe082", normal: "#a5d6a7" };
    return {
        padding: "14px 16px",
        borderRadius: "10px",
        border: "1px solid " + (borderMap[urgency] || borderMap.normal),
        background: bgMap[urgency] || bgMap.normal,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "10px",
    };
});

export const FPRenewalInfo = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
});

export const FPRenewalMethod = styled("div")({
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#333",
});

export const FPRenewalDate = styled("div")({
    fontSize: "0.8rem",
    color: "#666",
});

/* ─── Form Action Bar ─── */
export const FPActionBar = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    padding: "16px 0 0",
    borderTop: "1px solid #eee",
    marginTop: "16px",
    flexWrap: "wrap",
});

/* ─── Disclosure Toggle (collapsible) ─── */
export const FPDisclosure = styled("div")({
    marginBottom: "12px",
});

export const FPDisclosureToggle = styled("button")(function (props) {
    return {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 14px",
        border: "1px solid #e0e0e0",
        borderRadius: props.open ? "8px 8px 0 0" : "8px",
        background: props.open ? PRIMARY_BG : "#fafafa",
        cursor: "pointer",
        fontSize: "0.85rem",
        fontWeight: 600,
        color: props.open ? PRIMARY : "#555",
        transition: "all 0.2s",
    };
});

export const FPDisclosureContent = styled("div")({
    padding: "12px 14px",
    border: "1px solid #e0e0e0",
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    background: "#fff",
});

/* ─── Status Badge ─── */
export const FPStatusBadge = styled("span")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 12px",
        borderRadius: "16px",
        fontSize: "0.75rem",
        fontWeight: 700,
        background: color + "18",
        color: color,
    };
});
